import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    RiArrowLeftLine,
    RiArrowGoBackLine,
    RiArrowGoForwardLine,
    RiSaveLine,
    RiDownloadLine,
    RiKeyboardLine,
    RiPrinterLine,
} from 'react-icons/ri'
import toast from 'react-hot-toast'
import { useEditorStore } from '@/stores/useEditorStore'
import { getCanvasInstance } from '@/lib/canvasInstance'
import { useModalStore } from '@/stores/useModalStore'
import { ROUTES, buildRoute } from '@/constants/routes'
import { ShortcutsPanel } from '@/components/editor/ShortcutsPanel/ShortcutsPanel'
import { ExportModal } from '@/components/modals/ExportModal'
import styles from './EditorToolbar.module.css'

interface EditorToolbarProps {
    projectName: string
    projectId: string
    onSave?: () => void
}

export function EditorToolbar({ projectName, projectId, onSave }: EditorToolbarProps) {
    const navigate = useNavigate()
    const { undoStack, redoStack, isSaving, isDirty } = useEditorStore()
    const [showShortcuts, setShowShortcuts] = useState(false)

    const handleUndo = useCallback(() => {
        const canvas = getCanvasInstance()
        if (!canvas) return
        const json = useEditorStore.getState().undo()
        if (json) {
            canvas.loadFromJSON(JSON.parse(json)).then(() => canvas.renderAll())
        }
    }, [])

    const handleRedo = useCallback(() => {
        const canvas = getCanvasInstance()
        if (!canvas) return
        const json = useEditorStore.getState().redo()
        if (json) {
            canvas.loadFromJSON(JSON.parse(json)).then(() => canvas.renderAll())
        }
    }, [])

    const handleSave = useCallback(() => {
        if (onSave) {
            onSave()
        } else {
            toast.success('Guardado')
        }
    }, [onSave])

    const handleExport = useCallback(() => {
        useModalStore.getState().openModal(<ExportModal />)
    }, [])

    const handlePrint = useCallback(() => {
        if (onSave) onSave()
        navigate(buildRoute.orderCheckout(projectId))
    }, [onSave, navigate, projectId])

    return (
        <div className={styles.toolbar}>
            <div className={styles.left}>
                <button className={styles.iconButton} onClick={() => navigate(ROUTES.projects)} title="Volver">
                    <RiArrowLeftLine />
                </button>
                <div className={styles.divider} />
                <span className={styles.projectName}>{projectName}</span>
                <div className={styles.statusDot}>
                    {isSaving ? (
                        <span className={`${styles.dot} ${styles.dotSaving}`} />
                    ) : isDirty ? (
                        <span className={`${styles.dot} ${styles.dotUnsaved}`} />
                    ) : (
                        <span className={`${styles.dot} ${styles.dotSaved}`} />
                    )}
                    <span className={styles.statusText}>
                        {isSaving ? 'Guardando...' : isDirty ? 'Sin guardar' : 'Guardado'}
                    </span>
                </div>
            </div>

            <div className={styles.center}>
                <button className={styles.iconButton} disabled={undoStack.length === 0} onClick={handleUndo} title="Deshacer (Ctrl+Z)">
                    <RiArrowGoBackLine />
                </button>
                <button className={styles.iconButton} disabled={redoStack.length === 0} onClick={handleRedo} title="Rehacer (Ctrl+Shift+Z)">
                    <RiArrowGoForwardLine />
                </button>
                <button className={styles.shortcutsButton} onClick={() => setShowShortcuts(!showShortcuts)} title="Atajos de teclado">
                    <RiKeyboardLine />
                </button>
            </div>

            <div className={styles.right}>
                <button className={styles.iconButton} onClick={handleSave} title="Guardar (Ctrl+S)">
                    <RiSaveLine />
                </button>
                <button className={styles.printButton} onClick={handlePrint} title="Imprimir">
                    <RiPrinterLine />
                    <span>Imprimir</span>
                </button>
                <button className={styles.exportButton} onClick={handleExport} title="Exportar diseño">
                    <RiDownloadLine />
                    <span>Exportar</span>
                </button>
            </div>

            <ShortcutsPanel visible={showShortcuts} onClose={() => setShowShortcuts(false)} />
        </div>
    )
}
